import { useState } from "react";
import { feedItems } from "@/mocks/homeData";
import SectionHeading from "@/pages/home/components/SectionHeading";

const tabs = [
  { key: "latest", label: "最新快讯", icon: "ri-flashlight-line" },
  { key: "hot", label: "最热话题", icon: "ri-fire-line" },
  { key: "discussed", label: "神评最多", icon: "ri-chat-smile-line" },
];

const parseCount = (value: string): number => {
  const num = Number.parseFloat(value.replace(/[^0-9.]/g, ""));
  if (Number.isNaN(num)) return 0;
  return value.includes("万") ? num * 10000 : num;
};

export default function TimelineSection() {
  const [tab, setTab] = useState("latest");

  const sorted = [...feedItems].sort((a, b) => {
    if (tab === "hot") return parseCount(b.heat) - parseCount(a.heat);
    if (tab === "discussed") return parseCount(b.comments) - parseCount(a.comments);
    return 0;
  });

  const ranking = [...feedItems]
    .sort((a, b) => parseCount(b.heat) - parseCount(a.heat))
    .slice(0, 6);

  return (
    <section id="topics" className="w-full border-t border-background-200 bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full px-4 md:px-8">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            eyebrow="Feed · 热门话题"
            title="一条时间线，看清话题怎么发酵"
            desc="热门话题以快讯流形态实时更新，同一事件按时间串联，点击任意节点即可回看完整讨论脉络。"
          />
          <div className="flex items-center gap-1 self-start rounded-full border border-background-200 bg-background-100 p-1 lg:self-auto">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-medium transition-colors md:text-sm ${
                  tab === item.key
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={item.icon}></i>
                </span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <ol className="relative border-l border-background-300/70 pl-6 md:pl-8">
              {sorted.map((item) => (
                <li key={item.id} className="relative pb-8 last:pb-0">
                  <span className="absolute -left-[31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-primary-500 bg-background-50 md:-left-[39px]">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-500"></span>
                  </span>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                    <span className="font-label font-medium tabular-nums text-foreground-700">{item.time}</span>
                    <span className="flex items-center gap-1 whitespace-nowrap rounded-sm bg-secondary-100 px-2 py-0.5 text-secondary-800">
                      <span className="flex h-3 w-3 items-center justify-center">
                        <i className="ri-folder-2-line"></i>
                      </span>
                      {item.circle}
                    </span>
                    {item.verified ? (
                      <span className="flex items-center gap-1 whitespace-nowrap text-primary-600">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-verified-badge-line"></i>
                        </span>
                        已核验
                      </span>
                    ) : null}
                  </div>

                  <article className="mt-3 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
                    <div className={item.image ? "grid grid-cols-1 sm:grid-cols-[1fr_180px]" : ""}>
                      <div className="p-5">
                        <h4 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                          {item.title}
                        </h4>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>

                        <div className="mt-3 flex flex-wrap gap-2">
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-500">
                              <i className="ri-fire-line"></i>
                            </span>
                            {item.heat}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="flex h-3.5 w-3.5 items-center justify-center">
                              <i className="ri-chat-3-line"></i>
                            </span>
                            {item.comments} 条讨论
                          </span>
                          <a
                            href="#topics"
                            className="ml-auto flex items-center gap-1 whitespace-nowrap font-medium text-primary-600 transition-colors hover:text-primary-700"
                          >
                            查看完整脉络
                            <i className="ri-arrow-right-line"></i>
                          </a>
                        </div>
                      </div>

                      {item.image ? (
                        <div className="order-first h-40 w-full overflow-hidden bg-background-200 sm:order-last sm:h-full">
                          <img
                            src={item.image}
                            alt={`${item.title} 话题 黑料社区`}
                            title={`${item.circle} 话题 黑料社区`}
                            className="h-full w-full object-cover object-top"
                          />
                        </div>
                      ) : null}
                    </div>
                  </article>
                </li>
              ))}
            </ol>

            <a
              href="#top"
              className="mt-6 flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-loop-right-line"></i>
              </span>
              加载更早的快讯记录
            </a>
          </div>

          <aside className="flex flex-col gap-5 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-fire-line text-sm"></i>
                </span>
                <h4 className="font-heading text-base font-bold text-foreground-950">话题热度榜</h4>
              </div>
              <ol className="mt-4 space-y-3">
                {ranking.map((item, index) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm font-label text-xs font-bold ${
                        index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground-700">{item.title}</p>
                      <p className="mt-0.5 text-xs text-foreground-400">
                        {item.circle} · 热度 {item.heat}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-200 text-accent-800">
                  <i className="ri-compass-3-line text-sm"></i>
                </span>
                <h4 className="font-heading text-base font-bold text-foreground-950">怎么读一条脉络</h4>
              </div>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex gap-2">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-accent-700">
                    <i className="ri-checkbox-blank-circle-fill text-[6px]"></i>
                  </span>
                  先看时间戳，确认话题出现的最早节点
                </li>
                <li className="flex gap-2">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-accent-700">
                    <i className="ri-checkbox-blank-circle-fill text-[6px]"></i>
                  </span>
                  再看标签，判断它横跨了哪些圈子
                </li>
                <li className="flex gap-2">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-accent-700">
                    <i className="ri-checkbox-blank-circle-fill text-[6px]"></i>
                  </span>
                  最后进群组，看同好整理的对照与澄清
                </li>
              </ul>
              <a
                href="#zones"
                className="mt-4 flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 text-sm font-medium text-accent-800 transition-colors hover:bg-accent-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-layout-grid-line"></i>
                </span>
                按分区找话题
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}